<script lang="ts">
  import QueryStatus from '$lib/components/ui/QueryStatus.svelte';
  import { relativeTime, formatIST } from '$lib/utils/dates';
  import { storedColor } from '$lib/utils/colors';
  import { formatMoney } from '$lib/utils/money';
  import { createQuery } from '@tanstack/svelte-query';
  import {
    Inbox,
    Kanban,
    TriangleAlert,
    StickyNote,
    CheckSquare,
    PhoneCall,
    CalendarClock,
    Mail,
    ArrowRight
  } from 'lucide-svelte';
  import { dashboardApi } from '$lib/api/dashboard';
  import type {
    DashboardVertical,
    DashboardActivityItem,
    DashboardAttentionItem,
    AttentionTargetView
  } from '$lib/api/dashboard';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import { trendToSpark } from '$lib/utils/sparkline';
  import CustomerCareDashboard from './CustomerCareDashboard.svelte';
  import CoordinatorWorkspace from './CoordinatorWorkspace.svelte';
  import OwnedSalesWork from './OwnedSalesWork.svelte';
  import DailyMeetings from './DailyMeetings.svelte';
  import { roleProfile, rolePresentation, defaultDashboardMode, dashboardPreferenceKey, workflowAccessKey } from '$lib/utils/roleProfile';

  const { onNavigate }: { onNavigate: (tab: string) => void } = $props();

  const profile = $derived(roleProfile(session.me));
  let choice = $state<{ userId: number; profile: string; mode: 'care' | 'sales' } | null>(null);
  const mode = $derived.by((): 'care' | 'sales' => {
    const me = session.me;
    if (!me) return 'sales';
    if (choice?.userId === me.user.id && choice.profile === profile) return choice.mode;
    try {
      const saved = localStorage.getItem(dashboardPreferenceKey(me.user.id, profile));
      if (saved === 'care' || saved === 'sales') return saved;
    } catch { /* Storage can be disabled; retain the session choice. */ }
    return defaultDashboardMode(profile);
  });
  function chooseMode(value: 'care' | 'sales') {
    if (!session.me) return;
    choice = { userId: session.me.user.id, profile, mode: value };
    try { localStorage.setItem(dashboardPreferenceKey(session.me.user.id, profile), value); } catch { /* Nonessential preference only. */ }
  }

  // ===================== scope =====================
  // session.selectedVertical is a read-only derived view over the URL's
  // `?vertical=` query param (see session.svelte.ts) — the single source of
  // truth for scope, shared by the header's switcher and every page. Chip
  // clicks here just push the new value through the URL like the header does.
  function selectScope(id: number | 'all') {
    route.push({ ...route.current, vertical: String(id) });
  }

  const query = createQuery(() => ({
    enabled: profile !== 'unavailable' && mode === 'sales' && (session.isAdmin || !!session.me?.verticals.length),
    queryKey: ['dashboard', profile, session.me?.user.id, session.selectedVertical, workflowAccessKey(session.me)],
    queryFn: () => dashboardApi.get(session.selectedVertical),
    refetchOnWindowFocus: true
  }));
  // Never carry figures from another user/role/scope as placeholder data.
  const isSwitching = $derived(query.isFetching && !query.isLoading);

  // ===================== greeting =====================
  function timeGreeting(): string {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }
  const firstName = $derived((session.me?.user.name || session.me?.user.email || 'there').split(/\s+/)[0]);
  const greeting = $derived(`${timeGreeting()}, ${firstName}`);
  const eyebrow = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });

  // ===================== money / number formatting =====================
  // Ported from the mock's fmtL: lakh = 1e7 minor units, crore = 1e9 minor —
  // small values print as a whole-number lakh figure, crore-and-up gets one
  // decimal place (₹12.0Cr, ₹1.4Cr, ₹1.9Cr, ...).
  function formatMoneyMinor(minor: number, currency = 'INR'): string {
    return formatMoney(minor / 100, currency);
  }
  function formatDeltaMoney(deltaMinor: number | null, currency = 'INR'): string | null {
    if (deltaMinor === null) return null;
    if (deltaMinor === 0) return 'No change';
    const sign = deltaMinor > 0 ? '+' : '-';
    return `${sign}${formatMoneyMinor(Math.abs(deltaMinor), currency)} this month`;
  }
  function formatDeltaCount(n: number, suffix: string): string {
    if (n === 0) return `0 ${suffix}`;
    return `${n > 0 ? '+' : ''}${n} ${suffix}`;
  }
  function formatDeltaPts(pts: number | null): string | null {
    if (pts === null) return null;
    if (pts === 0) return 'No change';
    return `${pts > 0 ? '+' : ''}${pts} pts`;
  }
  function formatDeltaPct(pct: number | null, suffix: string): string | null {
    if (pct === null) return null;
    return `${pct > 0 ? '+' : ''}${pct}% ${suffix}`;
  }

  function timeAgo(iso: string): string {
    return relativeTime(iso);
  }

  // ===================== vertical chip palette =====================
  // Stable per-slug colors matching the mock (lime/teal/orange/steel); any
  // vertical outside that set (e.g. homeowners, exports) cycles the same
  // four so it's still stable, just not hand-picked.
  const VERT_DOT_BY_SLUG: Record<string, string> = {
    lighting: '#97D700',
    hotels: '#226A73',
    dealers: '#D08700',
    keyaccounts: '#5F9CA3'
  };
  const VERT_DOT_FALLBACK = ['#97D700', '#226A73', '#D08700', '#5F9CA3'];
  function verticalDot(v: DashboardVertical, idx: number): string {
    return VERT_DOT_BY_SLUG[v.slug] ?? VERT_DOT_FALLBACK[idx % VERT_DOT_FALLBACK.length];
  }
  function isActiveScope(id: number | 'all'): boolean {
    return session.selectedVertical === id;
  }

  // ===================== pipeline by stage =====================
  function stageColor(stage: string) { return storedColor(session.me?.verticals.find(v => v.id === session.selectedVertical)?.settings?.stage_colors?.[stage]); }

  // ===================== issues =====================
  const ISSUE_DOT = { open: '#D08700', in_progress: '#226A73', resolved: '#97D700' };

  // ===================== needs attention =====================
  const ATTENTION_META: Record<
    DashboardAttentionItem['type'],
    { icon: typeof Inbox; color: string; tint: string; label: string }
  > = {
    lead_untouched: { icon: Inbox, color: '#5c7a00', tint: 'var(--tym-lime-100)', label: 'Leads' },
    issue_unassigned: { icon: TriangleAlert, color: 'var(--tym-danger)', tint: '#FBEAE6', label: 'Issues' },
    deal_stale: { icon: Kanban, color: '#8a5c00', tint: '#FBF0DC', label: 'Deals' },
    activity_overdue: { icon: CheckSquare, color: 'var(--tym-teal)', tint: 'var(--tym-teal-100)', label: 'Activities' }
  };

  // leads/issues already have an id-keyed detail view wired into the hash
  // router (#/leads/<id>, #/issues/<id>) — honoring it here is free. deals
  // and activities don't have an id-scoped view yet, so those just switch
  // tabs (see handback notes).
  function openAttention(a: DashboardAttentionItem) {
    goToTarget(a.target_view, a.target_id);
  }
  function goToTarget(view: AttentionTargetView, id: number) {
    if (view === 'leads' || view === 'issues' || view === 'deals' || view === 'activities') {
      route.push({ tab: view, id });
    } else {
      onNavigate(view);
    }
  }

  // ===================== recent activity =====================
  const ACTIVITY_META: Record<string, { icon: typeof StickyNote; color: string; tint: string }> = {
    note: { icon: StickyNote, color: 'var(--tym-teal)', tint: 'var(--tym-teal-100)' },
    task: { icon: CheckSquare, color: 'var(--tym-navy)', tint: '#E6E9EE' },
    cold_call: { icon: PhoneCall, color: '#8a5c00', tint: '#FBF0DC' },
    call: { icon: PhoneCall, color: '#8a5c00', tint: '#FBF0DC' },
    meeting: { icon: CalendarClock, color: '#5c7a00', tint: 'var(--tym-lime-100)' },
    email: { icon: Mail, color: '#5a4b8a', tint: '#EAE6F3' }
  };
  function activityMeta(a: DashboardActivityItem) {
    return ACTIVITY_META[a.kind] ?? ACTIVITY_META.note;
  }

  const SOURCE_COLORS = ['#226A73', '#97D700', '#D08700'];
</script>

{#if profile === 'unavailable'}
  <p class="tym-page" role="alert">{rolePresentation[profile].subtitle}</p>
{:else}
  <header class="mx-auto max-w-[1280px] px-4 pt-5"><h1 class="font-heading text-2xl font-bold text-navy">Dashboard · {rolePresentation[profile].title}</h1><p class="mt-1 text-sm text-dark-gray">{rolePresentation[profile].subtitle}</p></header>
  <div class="mx-auto flex max-w-[1280px] flex-wrap gap-2 px-4 pt-4" aria-label="Dashboard view">
    <button type="button" class={mode === 'care' ? 'btn-primary' : 'btn-secondary'} aria-pressed={mode === 'care'} onclick={() => chooseMode('care')}>Customer care</button>
    <button type="button" class={mode === 'sales' ? 'btn-primary' : 'btn-secondary'} aria-pressed={mode === 'sales'} onclick={() => chooseMode('sales')}>Sales overview</button>
  </div>
{#if mode === 'care'}
  {#if profile === 'coordination'}<div class="mx-auto max-w-[1280px] px-4 pt-4"><DailyMeetings /></div>{/if}
  {#key `${session.me?.user.id}:${workflowAccessKey(session.me)}`}<CustomerCareDashboard />{/key}
{:else}
<div class="tym-page" style="max-width:1280px">
  <CoordinatorWorkspace />
  <div class="mb-5"><OwnedSalesWork section="pipeline" /></div>
  {#if !session.isAdmin && !session.me?.verticals.length}<p role="alert">Sales analytics require a full CRM grant. Your explicit support grants remain available in Customer care.</p>{/if}
  <h2 class="mb-2 mt-5 font-heading text-xl font-bold">{profile === 'oversight' ? 'Aggregate team performance' : 'Team sales analytics'}</h2>
  <p class="mb-4 text-sm text-dark-gray">These aggregate figures cover the selected CRM scope, not only contacts or deals owned by you. Use My day for {profile === 'oversight' ? 'team triage and ownership cleanup' : profile === 'coordination' ? 'your coordination agenda and calling queues' : 'your role-specific actionable work'}.</p>
  <!-- greeting + scope -->
  <div class="mb-[22px] flex flex-wrap items-end justify-between gap-4">
    <div>
      <div class="text-[12px] font-semibold uppercase tracking-[.08em] text-toyama-teal">{eyebrow}</div>
      <h2 class="mt-1 font-heading text-[26px] font-bold tracking-tight text-navy">{greeting}</h2>
    </div>
    {#if query.data}
      <div class="flex flex-wrap gap-2">
        {#if session.isAdmin}
          <button type="button" class="scope-chip" class:active={isActiveScope('all')} onclick={() => selectScope('all')}>
            <span class="scope-chip-dot" style="background:#97D700"></span>
            All verticals
          </button>
        {/if}
        {#each query.data.verticals as v, i (v.id)}
          <button type="button" class="scope-chip" class:active={isActiveScope(v.id)} onclick={() => selectScope(v.id)}>
            <span
              class="scope-chip-dot"
              style="background:{isActiveScope(v.id) ? '#97D700' : verticalDot(v, i)}"
            ></span>
            {v.name}
          </button>
        {/each}
      </div>
    {/if}
  </div>

  {#if query.isLoading}
    <QueryStatus loading label="dashboard" />
  {:else if query.isError}
    <QueryStatus error={query.error} label="dashboard" onRetry={() => query.refetch()} />
  {:else if query.data}
    {@const d = query.data}
    {@const pipeSpark = trendToSpark(d.pipeline.trend, 100, 30)}
    {@const leadsSpark = trendToSpark(d.leads.weekly_inflow, 100, 30)}
    {@const winSpark = d.win_rate.quarter_pct !== null ? trendToSpark(d.win_rate.trend, 100, 30) : null}
    {@const leadInflowSpark = trendToSpark(d.leads.weekly_inflow, 100, 38)}
    {@const issuesResolvedAll = Math.max(d.issues.total - d.issues.open - d.issues.in_progress, 0)}
    {@const maxStage = Math.max(1, ...d.pipeline.by_stage.map((s) => s.value_minor))}
    {@const scopeVerticalName = d.verticals.find((v) => String(v.id) === d.scope)?.name ?? ''}
    <div class="transition-opacity" class:opacity-60={isSwitching}>
      <!-- KPI row -->
      <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <!-- Open pipeline (dark) -->
        <div class="kpi-card-dark flex min-h-[150px] flex-col">
          <div class="flex items-baseline justify-between gap-2">
            <span class="text-[11px] font-semibold uppercase tracking-[.1em] text-white/55">Open pipeline</span>
            {#if formatDeltaMoney(d.pipeline.delta_month_minor, d.pipeline.currency || 'INR')}
              <span class="whitespace-nowrap font-heading text-[12px] font-semibold text-toyama-lime"
                >{formatDeltaMoney(d.pipeline.delta_month_minor, d.pipeline.currency || 'INR')}</span
              >
            {/if}
          </div>
          <div class="mt-2.5 font-heading text-[34px] font-bold leading-none tracking-tight text-white">
            {#if d.pipeline.by_currency?.length}
              {#each d.pipeline.by_currency as total (total.currency)}<div class="mb-2 text-[26px]" aria-label={`${total.currency} pipeline`}>{formatMoneyMinor(total.total_minor, total.currency)} <span class="text-xs font-normal text-white/70">{total.currency} · {total.deal_count} deals</span></div>{/each}
            {:else}{formatMoneyMinor(d.pipeline.total_minor, d.pipeline.currency || 'INR')}{/if}
          </div>
          <div class="mt-[5px] text-[12px] text-white/55">
            {d.pipeline.deal_count} open deal{d.pipeline.deal_count === 1 ? '' : 's'} &middot;
            {d.scope === 'all' ? `${d.verticals.length} verticals` : scopeVerticalName}
          </div>
          <div class="flex-1"></div>
          {#if pipeSpark}
            <svg viewBox="0 0 100 30" preserveAspectRatio="none" class="mt-2.5 block h-[34px] w-full">
              <path d={pipeSpark.area} fill="rgba(151,215,0,.16)"></path>
              <polyline
                points={pipeSpark.points}
                fill="none"
                stroke="#97D700"
                stroke-width="1.8"
                vector-effect="non-scaling-stroke"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></polyline>
            </svg>
          {/if}
        </div>

        <!-- New leads -->
        <div class="tym-card flex min-h-[150px] flex-col p-[18px_18px_14px] shadow-[var(--tym-shadow-2)]">
          <div class="flex items-baseline justify-between gap-2">
            <span class="text-[11px] font-semibold uppercase tracking-[.1em] text-dark-gray">New leads</span>
            <span class="whitespace-nowrap font-heading text-[12px] font-semibold" style="color:#5c7a00"
              >{formatDeltaCount(d.leads.delta_vs_last_week, 'vs last wk')}</span
            >
          </div>
          <div class="mt-2.5 font-heading text-[30px] font-bold leading-none tracking-tight text-navy">
            {d.leads.new_this_week}
          </div>
          <div class="mt-[5px] text-[12px] text-dark-gray">new this week</div>
          <div class="flex-1"></div>
          {#if leadsSpark}
            <svg viewBox="0 0 100 30" preserveAspectRatio="none" class="mt-2.5 block h-[34px] w-full">
              <path d={leadsSpark.area} fill="rgba(34,106,115,.08)"></path>
              <polyline
                points={leadsSpark.points}
                fill="none"
                stroke="#226A73"
                stroke-width="1.8"
                vector-effect="non-scaling-stroke"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></polyline>
            </svg>
          {/if}
        </div>

        <!-- Win rate -->
        <div class="tym-card flex min-h-[150px] flex-col p-[18px_18px_14px] shadow-[var(--tym-shadow-2)]">
          <div class="flex items-baseline justify-between gap-2">
            <span class="text-[11px] font-semibold uppercase tracking-[.1em] text-dark-gray">Win rate</span>
            {#if formatDeltaPts(d.win_rate.delta_pts)}
              <span class="whitespace-nowrap font-heading text-[12px] font-semibold" style="color:#5c7a00"
                >{formatDeltaPts(d.win_rate.delta_pts)}</span
              >
            {/if}
          </div>
          <div class="mt-2.5 font-heading text-[30px] font-bold leading-none tracking-tight text-navy">
            {d.win_rate.quarter_pct !== null ? `${d.win_rate.quarter_pct}%` : '—'}
          </div>
          <div class="mt-[5px] text-[12px] text-dark-gray">
            {d.win_rate.quarter_pct !== null ? 'last quarter' : 'no closed deals this quarter'}
          </div>
          <div class="flex-1"></div>
          {#if winSpark}
            <svg viewBox="0 0 100 30" preserveAspectRatio="none" class="mt-2.5 block h-[34px] w-full">
              <path d={winSpark.area} fill="rgba(34,106,115,.08)"></path>
              <polyline
                points={winSpark.points}
                fill="none"
                stroke="#226A73"
                stroke-width="1.8"
                vector-effect="non-scaling-stroke"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></polyline>
            </svg>
          {/if}
        </div>

        <!-- Open issues -->
        <div class="tym-card flex min-h-[150px] flex-col p-[18px_18px_14px] shadow-[var(--tym-shadow-2)]">
          <div class="flex items-baseline justify-between gap-2">
            <span class="text-[11px] font-semibold uppercase tracking-[.1em] text-dark-gray">Open issues</span>
            <span class="whitespace-nowrap font-heading text-[12px] font-semibold" style="color:#8a5c00"
              >{d.issues.in_progress} in progress</span
            >
          </div>
          <div class="mt-2.5 font-heading text-[30px] font-bold leading-none tracking-tight text-navy">
            {d.issues.open}
          </div>
          <div class="mt-[5px] text-[12px] text-dark-gray">
            {d.issues.resolved_week} resolved this wk &middot; {d.issues.total} total
          </div>
          <div class="flex-1"></div>
          {#if d.issues.total > 0}
            <div class="mt-[22px] flex h-2 overflow-hidden rounded-pill bg-[#F1F0EF]">
              <span
                class="block h-full"
                style="width:{Math.round((d.issues.open / d.issues.total) * 100)}%; background:{ISSUE_DOT.open}"
              ></span>
              <span
                class="block h-full"
                style="width:{Math.round(
                  (d.issues.in_progress / d.issues.total) * 100
                )}%; background:{ISSUE_DOT.in_progress}"
              ></span>
              <span
                class="block h-full"
                style="width:{Math.round(
                  (issuesResolvedAll / d.issues.total) * 100
                )}%; background:{ISSUE_DOT.resolved}"
              ></span>
            </div>
          {/if}
        </div>
      </div>

      <!-- row 2: pipeline by stage + lead inflow -->
      <div class="mb-5 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-[1.5fr_1fr]">
        <div class="tym-card p-[20px_22px_16px] shadow-[var(--tym-shadow-2)]">
          <div class="mb-3.5 flex items-center justify-between">
            <h3 class="font-heading text-[15px] font-bold text-navy">Pipeline by stage · {d.pipeline.currency || 'INR'}</h3>
            <button type="button" class="tym-link-btn" onclick={() => onNavigate('deals')}>
              View board
              <ArrowRight class="h-[13px] w-[13px]" strokeWidth={2.4} />
            </button>
          </div>
          {#if d.pipeline.by_stage.length === 0}
            <p class="tym-empty-state !py-4 !text-left">No open deals in this vertical.</p>
          {:else}
            <div class="flex flex-col">
              {#each d.pipeline.by_stage as st, i (st.stage)}
                <div class="flex items-center gap-3.5 py-2.5" class:stage-row-border={i > 0}>
                  <span class="flex min-w-0 flex-none basis-[128px] items-center gap-2">
                    <span
                      class="h-2 w-2 flex-none rounded-full"
                      style="background:{stageColor(st.stage)}"
                    ></span>
                    <span class="truncate text-[13px] font-semibold capitalize text-navy">{st.stage}</span>
                  </span>
                  <span class="block h-2.5 flex-1 overflow-hidden rounded-pill bg-[#F1F0EF]">
                    <span
                      class="block h-full rounded-pill transition-[width] duration-300"
                      style="width:{Math.round(
                        (st.value_minor / maxStage) * 100
                      )}%; background:{stageColor(st.stage)}"
                    ></span>
                  </span>
                  <span class="flex-none basis-[66px] text-right font-heading text-[14px] font-bold text-navy"
                    >{formatMoneyMinor(st.value_minor, d.pipeline.currency || 'INR')}</span
                  >
                  <span class="flex-none basis-[56px] text-right text-[11.5px] text-dark-gray"
                    >{st.count} deal{st.count === 1 ? '' : 's'}</span
                  >
                </div>
              {/each}
            </div>
          {/if}
        </div>

        <div class="tym-card flex flex-col p-[20px_22px_18px] shadow-[var(--tym-shadow-2)]">
          <div class="flex items-baseline justify-between">
            <h3 class="font-heading text-[15px] font-bold text-navy">Lead inflow</h3>
            <span class="text-[11.5px] text-dark-gray">Last 8 weeks</span>
          </div>
          <div class="mt-2.5 flex items-baseline gap-2.5">
            <span class="font-heading text-[26px] font-bold leading-none tracking-tight text-navy"
              >{d.leads.total_8w}</span
            >
            {#if formatDeltaPct(d.leads.pct_vs_prior_8w, 'vs prior 8 wks')}
              <span class="font-heading text-[12px] font-semibold" style="color:#5c7a00"
                >{formatDeltaPct(d.leads.pct_vs_prior_8w, 'vs prior 8 wks')}</span
              >
            {/if}
          </div>
          {#if leadInflowSpark}
            <svg viewBox="0 0 100 38" preserveAspectRatio="none" class="mt-3 block h-[76px] w-full">
              <path d={leadInflowSpark.area} fill="rgba(34,106,115,.1)"></path>
              <polyline
                points={leadInflowSpark.points}
                fill="none"
                stroke="#226A73"
                stroke-width="2"
                vector-effect="non-scaling-stroke"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></polyline>
            </svg>
          {/if}
          {#if d.leads.by_source.length > 0}
            <div class="mt-3.5 flex flex-col gap-2.5 border-t border-[#F0F0F0] pt-3">
              {#each d.leads.by_source as src, i (src.source)}
                <div class="flex items-center gap-2.5">
                  <span class="flex-none basis-[132px] truncate text-[12px] text-[#666]">{src.source}</span>
                  <span class="block h-1.5 flex-1 overflow-hidden rounded-pill bg-[#F1F0EF]">
                    <span
                      class="block h-full rounded-pill"
                      style="width:{src.pct}%; background:{SOURCE_COLORS[i % SOURCE_COLORS.length]}"
                    ></span>
                  </span>
                  <span class="flex-none basis-[34px] text-right font-heading text-[12px] font-bold text-navy"
                    >{src.pct}%</span
                  >
                </div>
              {/each}
            </div>
          {/if}
        </div>
      </div>

      <!-- row 3: needs attention + issues health / recent activity -->
      <div class="grid grid-cols-1 items-start gap-5 lg:grid-cols-[1.5fr_1fr]">
        <div class="tym-card p-[18px_22px_10px] shadow-[var(--tym-shadow-2)]">
          <div class="mb-1.5 flex items-center gap-2">
            <h3 class="font-heading text-[15px] font-bold text-navy">Needs attention</h3>
            {#if d.attention.length > 0}
              <span
                class="inline-flex h-5 min-w-[20px] items-center justify-center rounded-pill px-1.5 font-heading text-[11px] font-bold"
                style="background:#FBF0DC; color:#8a5c00"
              >
                {d.attention.length}
              </span>
            {/if}
          </div>
          {#if d.attention.length === 0}
            <p class="tym-empty-state !py-4 !text-left">Nothing needs attention right now.</p>
          {:else}
            <div class="flex flex-col">
              {#each d.attention as a, i (a.type + '-' + a.target_id)}
                {@const meta = ATTENTION_META[a.type]}
                <button
                  type="button"
                  class="flex w-full items-center gap-3 rounded-lg p-3 text-left hover:bg-toyama-light-carbon"
                  class:stage-row-border={i > 0}
                  onclick={() => openAttention(a)}
                >
                  <span
                    class="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px]"
                    style="background:{meta.tint}; color:{meta.color}"
                  >
                    <meta.icon class="h-4 w-4" strokeWidth={2} />
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block text-[13px] font-semibold leading-[1.35] text-navy">{a.title}</span>
                    {#if a.meta}
                      <span class="mt-0.5 block text-[11.5px] text-dark-gray">{a.meta}</span>
                    {/if}
                  </span>
                  <span class="inline-flex shrink-0 items-center gap-1 text-[11.5px] font-semibold text-toyama-teal">
                    {meta.label}
                    <ArrowRight class="h-3 w-3" strokeWidth={2.4} />
                  </span>
                </button>
              {/each}
            </div>
          {/if}
        </div>

        <div class="flex flex-col gap-5">
          <!-- issues health -->
          <div class="tym-card p-[18px_22px_16px] shadow-[var(--tym-shadow-2)]">
            <div class="mb-3.5 flex items-center justify-between">
              <h3 class="font-heading text-[15px] font-bold text-navy">Issues health</h3>
              <button type="button" class="tym-link-btn" onclick={() => onNavigate('issues')}>
                All issues
                <ArrowRight class="h-[13px] w-[13px]" strokeWidth={2.4} />
              </button>
            </div>
            {#if d.issues.total === 0}
              <p class="text-[12.5px] text-dark-gray">No issues reported in this vertical.</p>
            {:else}
              <div class="mb-3.5 flex h-2.5 overflow-hidden rounded-pill bg-[#F1F0EF]">
                <span
                  class="block h-full"
                  style="width:{Math.round((d.issues.open / d.issues.total) * 100)}%; background:{ISSUE_DOT.open}"
                ></span>
                <span
                  class="block h-full"
                  style="width:{Math.round(
                    (d.issues.in_progress / d.issues.total) * 100
                  )}%; background:{ISSUE_DOT.in_progress}"
                ></span>
                <span
                  class="block h-full"
                  style="width:{Math.round(
                    (issuesResolvedAll / d.issues.total) * 100
                  )}%; background:{ISSUE_DOT.resolved}"
                ></span>
              </div>
              <div class="flex flex-col gap-2">
                <div class="flex items-center gap-2.5">
                  <span class="h-2 w-2 flex-none rounded-full" style="background:{ISSUE_DOT.open}"></span>
                  <span class="flex-1 text-[12.5px] text-[#666]">Open</span>
                  <span class="font-heading text-[13px] font-bold text-navy">{d.issues.open}</span>
                </div>
                <div class="flex items-center gap-2.5">
                  <span class="h-2 w-2 flex-none rounded-full" style="background:{ISSUE_DOT.in_progress}"></span>
                  <span class="flex-1 text-[12.5px] text-[#666]">In progress</span>
                  <span class="font-heading text-[13px] font-bold text-navy">{d.issues.in_progress}</span>
                </div>
                <div class="flex items-center gap-2.5">
                  <span class="h-2 w-2 flex-none rounded-full" style="background:{ISSUE_DOT.resolved}"></span>
                  <span class="flex-1 text-[12.5px] text-[#666]">Resolved</span>
                  <span class="font-heading text-[13px] font-bold text-navy">{issuesResolvedAll}</span>
                </div>
              </div>
              {#if d.issues.oldest_open}
                {@const oldest = d.issues.oldest_open}
                <button
                  type="button"
                  class="mt-3 block w-full border-t border-[#F0F0F0] pt-2.5 text-left hover:opacity-80"
                  onclick={() => route.push({ tab: 'issues', id: oldest.id })}
                >
                  <div class="text-[12.5px] font-semibold leading-[1.35] text-navy">{oldest.title}</div>
                  <div class="mt-0.5 text-[11px] text-dark-gray" title={formatIST(oldest.created_at)}>
                    Oldest open &middot; created {timeAgo(oldest.created_at)}
                  </div>
                </button>
              {/if}
            {/if}
          </div>

          <!-- recent activity -->
          <div class="tym-card p-[14px_12px_8px] shadow-[var(--tym-shadow-2)]">
            <h3 class="m-0 mb-1 p-[4px_10px_0] font-heading text-[15px] font-bold text-navy">Recent activity</h3>
            {#if d.activity.length === 0}
              <p class="tym-empty-state">No recent activity.</p>
            {:else}
              {#each d.activity as a (a.id)}
                {@const meta = activityMeta(a)}
                <div class="flex gap-3 p-2.5">
                  <span
                    class="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px]"
                    style="background:{meta.tint}; color:{meta.color}"
                  >
                    <meta.icon class="h-[15px] w-[15px]" strokeWidth={2} />
                  </span>
                  <div class="min-w-0 flex-1">
                    <div class="text-[12.5px] leading-[1.4] text-toyama-heavy-carbon">
                      {a.summary || '(no summary)'}
                    </div>
                    <div class="mt-0.5 text-[11px] text-dark-gray" title={formatIST(a.created_at)}>
                      {a.vertical ? `${a.vertical} · ` : ''}{timeAgo(a.created_at)}
                    </div>
                  </div>
                </div>
              {/each}
            {/if}
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>
{/if}
{/if}

<style>
  .scope-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 34px;
    padding: 0 13px;
    border-radius: 999px;
    border: 1px solid var(--tym-cloudy-gray);
    background: var(--tym-white);
    color: #666;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color var(--tym-dur-fast) var(--tym-ease-out);
  }
  .scope-chip:hover {
    border-color: var(--tym-teal);
  }
  .scope-chip.active {
    background: var(--tym-navy);
    border-color: var(--tym-navy);
    color: var(--tym-white);
  }
  .scope-chip-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .kpi-card-dark {
    background: linear-gradient(135deg, #10223a 15%, #226a73 140%);
    border-radius: var(--tym-radius-lg);
    padding: 18px 20px 14px;
    box-shadow: 0 4px 12px rgba(16, 34, 58, 0.16);
  }
  .tym-link-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: none;
    background: transparent;
    color: var(--tym-teal);
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 0;
  }
  .tym-link-btn:hover {
    color: var(--tym-navy);
  }
  .stage-row-border {
    border-top: 1px solid #f0f0f0;
  }
</style>
